import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Box, IconButton, Typography } from '@mui/material';
import { useEffect } from 'react';
import useFamilyLevelFacets from '../hooks/useFamilyLevelFacets';
import FamilyLevelList from './FamilyLevelList';

interface DepartmentsColumnProps {
  pathPrefix: string[];
  selectedCode?: string;
  title: string;
  image?: string;
  width: number;
  showDivider?: boolean;
  onClose?: () => void;
  onHover: (code: string) => void;
  onSelect: (code: string) => void;
  onSeeAll?: () => void;
  onSeeAllHover?: () => void;
  seeAllLabel?: string;
  onCollapse?: () => void;
}

export default function DepartmentsColumn({ pathPrefix, selectedCode, title, image, width, showDivider, onClose, onHover, onSelect, onSeeAll, onSeeAllHover, seeAllLabel, onCollapse }: DepartmentsColumnProps): React.JSX.Element {
  const depth = pathPrefix.length;
  const { items, isLoading } = useFamilyLevelFacets(pathPrefix);

  useEffect(() => {
    if (!onCollapse || isLoading || items.length > 0) {
      return;
    }

    onCollapse();
  }, [isLoading, items.length]);

  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        py: 2,
        overflow: 'hidden',
        borderRight: showDivider ? '1px solid' : 'none',
        borderColor: 'outlineVariant.main',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          minHeight: depth > 0 ? 64 : 40,
          px: 2,
        }}
      >
        {image && <Avatar src={image} size={48} />}
        <Typography variant={depth === 0 ? 'titleLarge' : 'titleMedium'} noWrap sx={{ flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {onClose && (
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <FamilyLevelList
        items={items}
        isLoading={isLoading}
        depth={depth}
        selectedCode={selectedCode}
        onHover={onHover}
        onSelect={onSelect}
        onSeeAll={onSeeAll}
        onSeeAllHover={onSeeAllHover}
        seeAllLabel={seeAllLabel}
      />
    </Box>
  );
}
